import { useEffect, useRef, useState } from "react";
import type { AIInsight } from "../../lib/types";
import { ShareButton } from "./ShareButton";

// ── Social card content ───────────────────────────────────────────────────────

interface ManifestoSocialContentProps {
  insight: AIInsight;
}

export function ManifestoSocialContent({ insight }: ManifestoSocialContentProps) {
  const paragraphs = insight.content
    .split(/\n\n|\n/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0)
    .slice(0, 3); // keep card compact

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 20,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "#DB1270",
          marginBottom: 4,
        }}
      >
        Manifest
      </div>
      {paragraphs.map((para, i) => (
        <p
          key={i}
          style={{
            fontSize: 22,
            fontWeight: 400,
            lineHeight: 1.5,
            color: "#000000",
            letterSpacing: "-0.005em",
            margin: 0,
          }}
        >
          {para}
        </p>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface Props {
  insight: AIInsight | null;
  eventTitle?: string;
}

export function ManifestoCard({ insight, eventTitle = "" }: Props) {
  // Track which insight is currently displayed (for crossfade)
  const [displayedInsight, setDisplayedInsight] = useState<AIInsight | null>(insight);
  const [opacity, setOpacity] = useState(1);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // On first mount, just set directly (no animation needed)
    if (displayedInsight === null && insight !== null) {
      setDisplayedInsight(insight);
      return;
    }

    const incomingId = insight?.id ?? null;
    const displayedId = displayedInsight?.id ?? null;

    if (incomingId === displayedId) return; // No change

    // Fade out
    setOpacity(0);

    if (fadeTimerRef.current !== null) {
      clearTimeout(fadeTimerRef.current);
    }

    // After fade-out completes (~250ms), swap content and fade back in
    fadeTimerRef.current = setTimeout(() => {
      setDisplayedInsight(insight);
      setOpacity(1);
    }, 270);

    return () => {
      if (fadeTimerRef.current !== null) {
        clearTimeout(fadeTimerRef.current);
      }
    };
  // We only want to react to insight.id changes, not to the entire object reference
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insight?.id]);

  return (
    <div className="bg-rc-cream rounded-2xl p-8 md:p-12 lg:p-16">
      <div
        style={{
          opacity,
          transition: "opacity 250ms ease",
        }}
      >
        {displayedInsight ? (
          <div>
            <div className="flex items-start justify-between gap-4 mb-6">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-rc-berry">
                Manifest
              </div>
              <ShareButton
                label="Manifest teilen"
                title={eventTitle}
                cardContent={() => <ManifestoSocialContent insight={displayedInsight} />}
              />
            </div>
            <div className="flex flex-col gap-6">
              {displayedInsight.content
                .split(/\n\n|\n/)
                .map((para) => para.trim())
                .filter((para) => para.length > 0)
                .map((para, i) => (
                  <p
                    key={i}
                    className="text-xl md:text-2xl lg:text-3xl 2xl:text-4xl font-normal leading-[1.5] text-rc-black tracking-[-0.005em]"
                  >
                    {para}
                  </p>
                ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 md:py-24 text-center">
            <div className="w-12 h-12 rounded-full bg-rc-fog/60 flex items-center justify-center mb-5">
              <span
                className="text-[22px] leading-none select-none"
                role="img"
                aria-label="Manifest"
              >
                ✦
              </span>
            </div>
            <p className="text-[15px] leading-[1.6] text-rc-charcoal max-w-[28rem]">
              Das Manifest wird erstellt, sobald genug Stimmen vorliegen…
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
